import {
  AiChSuggestionMessage,
  AiChattingService,
  SendMessageRequest,
  SuggestionDto,
} from '@api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'

import { $fanId } from '@/models/chattingUI/chats/chats'
import { $creatorId, $creatorOFId } from '@/models/creators'
import { toast } from '@/models/toasts'

import {
  $chatterLanguage,
  $isTranslatorEnabled,
  translateTextsFx,
} from '../messages/translations'

export type SendMessageRequestWithTranslation = SendMessageRequest & {
  translatedText?: string
}

export type SuggestionDtoWithTranslation = Omit<SuggestionDto, 'messages'> & {
  messages: Array<SendMessageRequestWithTranslation>
}

export type AiChSuggestionMessageWithTranslation = Omit<
  AiChSuggestionMessage,
  'suggestions'
> & {
  suggestions?: Array<SuggestionDtoWithTranslation>
  isTranslated?: boolean
  translatedLanguage?: string
}

export const setChatSuggestions =
  createEvent<AiChSuggestionMessageWithTranslation>('set chat suggestions')
export const chatSuggestionsUpdated =
  createEvent<AiChSuggestionMessageWithTranslation>('chat suggestions updated')
export const clearChatSuggestions = createEvent('clear chat suggestions')
export const clearChatSuggestionsByVersion = createEvent<{
  version: number
  fanId: number
}>('clear chat suggestions by version')
export const clearSuggestionsTranslations = createEvent<number>(
  'clear suggestions translations',
)

export const $chatSuggestions = createStore<
  Record<number, AiChSuggestionMessageWithTranslation | null>
>(
  {},
  {
    name: 'chat suggestions',
  },
)
  .on(setChatSuggestions, (state, suggestions) => {
    if (!suggestions?.ofFanId) return state

    const currentSuggestion = state[suggestions.ofFanId]

    if (currentSuggestion?.version === suggestions.version) {
      return state
    }

    return {
      ...state,
      [suggestions.ofFanId]: suggestions,
    }
  })
  .on(clearChatSuggestionsByVersion, (state, data) => {
    const currentSuggestion = state[data.fanId]

    if (currentSuggestion?.version === data.version) {
      return {
        ...state,
        [data.fanId]: null,
      }
    }

    return state
  })
  .on(clearSuggestionsTranslations, (state, fanId) => {
    const currentSuggestion = state[fanId]

    if (!currentSuggestion) return state

    return {
      ...state,
      [fanId]: {
        ...currentSuggestion,
        isTranslated: false,
        translatedLanguage: undefined,
        suggestions: currentSuggestion.suggestions?.map((suggestion) => ({
          ...suggestion,
          messages: suggestion.messages.map((message) => ({
            ...message,
            translatedText: undefined,
          })),
        })),
      },
    }
  })
  .reset(clearChatSuggestions)

export const $hasChatSuggestions = $chatSuggestions.map((suggestionsMap) => {
  return Object.values(suggestionsMap).some(
    (suggestions) =>
      suggestions !== null &&
      suggestions.suggestions &&
      suggestions?.suggestions?.length > 0,
  )
})

export const $areSuggestionsTranslated = combine(
  $chatSuggestions,
  $fanId,
  (suggestions, fanId) => {
    if (!fanId) return false

    const currentSuggestion = suggestions[fanId]

    return currentSuggestion?.isTranslated ?? false
  },
)

export const $suggestionsTranslatedLanguage = combine(
  $chatSuggestions,
  $fanId,
  (suggestions, fanId) => {
    if (!fanId) return null

    const currentSuggestion = suggestions[fanId]

    return currentSuggestion?.translatedLanguage ?? null
  },
)

export const translateSuggestionsFx = attach({
  name: 'translate suggestions',
  source: { suggestions: $chatSuggestions, language: $chatterLanguage },
  mapParams: (fanId: number, { suggestions, language }) => {
    const currentSuggestion = suggestions[fanId]

    if (!currentSuggestion) {
      throw new Error('No suggestions found for this fan')
    }

    return { fanId, currentSuggestion, language }
  },
  effect: createEffect(
    async ({
      fanId,
      currentSuggestion,
      language,
    }: {
      fanId: number
      currentSuggestion: AiChSuggestionMessageWithTranslation
      language: string | null
    }) => {
      if (
        (currentSuggestion.isTranslated &&
          currentSuggestion.translatedLanguage === language) ||
        !language
      ) {
        return { fanId, translatedSuggestion: currentSuggestion }
      }

      const textsToTranslate: string[] = []

      currentSuggestion.suggestions?.forEach((suggestion) => {
        suggestion.messages.forEach((message) => {
          if (message.text) {
            textsToTranslate.push(message.text)
          }
        })
      })

      if (textsToTranslate.length === 0) {
        return { fanId, translatedSuggestion: currentSuggestion }
      }

      const translations = await translateTextsFx({
        texts: textsToTranslate,
        language,
      })

      const translationMap = new Map<string, string>()

      Array.from(textsToTranslate).forEach((text, index) => {
        translationMap.set(text, translations[index])
      })

      const updatedSuggestion: AiChSuggestionMessageWithTranslation = {
        ...currentSuggestion,
        isTranslated: true,
        translatedLanguage: language ?? undefined,
        suggestions: currentSuggestion.suggestions?.map((suggestion) => ({
          ...suggestion,
          messages: suggestion.messages.map((message) => {
            if (message.text && translationMap.has(message.text)) {
              return {
                ...message,
                translatedText: translationMap.get(message.text),
              }
            }

            return message
          }),
        })),
      }

      return { fanId, translatedSuggestion: updatedSuggestion }
    },
  ),
})

// Update store with translated suggestions
$chatSuggestions.on(
  translateSuggestionsFx.doneData,
  (state, { fanId, translatedSuggestion }) => ({
    ...state,
    [fanId]: translatedSuggestion,
  }),
)

// Clear translations when translation is disabled
sample({
  clock: $isTranslatorEnabled,
  source: $fanId,
  filter: (fanId, isAutoEnabled) => !isAutoEnabled && !!fanId,
  fn: (fanId) => fanId!,
  target: clearSuggestionsTranslations,
})

export const regenerateSuggestionsFx = attach({
  name: 'regenerate chat suggestions',
  source: {
    creatorId: $creatorId,
    creatorOFId: $creatorOFId,
    fanId: $fanId,
  },
  mapParams: (_: void, { creatorId, creatorOFId, fanId }) => {
    if (!creatorId || !creatorOFId || !fanId) {
      throw new Error('Missing required data for AICH regeneration')
    }

    return {
      creatorId,
      ofCreatorId: creatorOFId,
      ofFanId: fanId,
    }
  },
  effect: createEffect(AiChattingService.regenerateSuggestion),
})

regenerateSuggestionsFx.doneData.watch(() => {
  toast.success(
    'Suggestions regeneration started. Please wait for new suggestions.',
  )
})

regenerateSuggestionsFx.failData.watch(() => {
  toast.error("Couldn't generate a suggestion. Try again.")
})
